import { LuIcon } from "@overmap-ai/blocks"
import { EMPTY_ARRAY } from "@overmap-ai/common"
import { use, useCallback } from "react"

import { ProcedureStepCompletionStatusMultiSelect } from "../../../components/DropdownMultiSelects/ProcedureStepCompletionStatusMultiSelect"
import { ProcedureStepCompletionStatusSelect } from "../../../components/DropdownSelects/ProcedureStepCompletionStatusSelect"
import {
	ProcedureStepCompletionStatusIcon,
	ProcedureStepCompletionStatusIcons,
} from "../../../components/Icons/ProcedureStepCompletionStatusIcon"
import { ProcedureStepCompletionStatus } from "../../../enums/procedures"
import { modelIconMapping } from "../../../mappings/icons"
import { procedureStepCompletionStatusData } from "../../../mappings/procedures"
import { FilterModifierDropdown, RemoveFilterButton } from "../../components"
import { FilterCellBadge } from "../../components/FilterCellBadge"
import { FilterCellButton } from "../../components/FilterCellButton"
import { FilterCellRoot } from "../../components/FilterCellRoot"
import { FilterRendererContext } from "../../contexts/FilterRendererContext/context"
import type { FilterDefRenderProps } from "../BaseFilterDef"
import type { ProcedureStepCompletionStatusFilterDef } from "./ProcedureStepCompletionStatusFilterDef"

type ProcedureStepCompletionStatusFilterCellProps = FilterDefRenderProps<ProcedureStepCompletionStatusFilterDef>

export const ProcedureStepCompletionStatusFilterCell = (props: ProcedureStepCompletionStatusFilterCellProps) => {
	const { filter, filterDef, filterManager } = props

	const { size } = use(FilterRendererContext)
	const { procedureStep } = filterDef
	const filterValue = filter.getFilterValue()
	const filterModifier = filter.getFilterModifier()

	const handleValueChange = useCallback(
		(value?: ProcedureStepCompletionStatus) => {
			filter.setFilterValue(value)
		},
		[filter],
	)

	const handleValuesChange = useCallback(
		(values: ProcedureStepCompletionStatus[]) => {
			if (values.length > 0) {
				filter.setFilterValue(values)
			} else {
				filter.setFilterValue(undefined)
			}
		},
		[filter],
	)

	const getFilterValueUi = () => {
		switch (filterModifier) {
			case "equals":
			case "notEquals": {
				const equalsModifier = filterDef.modifiers.equals
				const equalsModifierFilterValue =
					filterValue !== undefined && equalsModifier.isFilterValueValid(filterValue)
						? filterValue
						: undefined

				return (
					<ProcedureStepCompletionStatusSelect
						required={false}
						value={equalsModifierFilterValue}
						onValueChange={handleValueChange}
						size={size}
					>
						<FilterCellButton className="truncate">
							{equalsModifierFilterValue !== undefined ? (
								<>
									<ProcedureStepCompletionStatusIcon status={equalsModifierFilterValue} />
									{procedureStepCompletionStatusData[equalsModifierFilterValue].label}
								</>
							) : (
								"..."
							)}
						</FilterCellButton>
					</ProcedureStepCompletionStatusSelect>
				)
			}
			case "includes":
			case "excludes": {
				const includesModifier = filterDef.modifiers.includes
				const includesModifierFilterValue =
					filterValue !== undefined && includesModifier.isFilterValueValid(filterValue)
						? filterValue
						: undefined

				const getLabelUi = () => {
					switch ((includesModifierFilterValue ?? EMPTY_ARRAY).length) {
						case 0:
							return "..."
						case 1:
							return (
								<>
									<ProcedureStepCompletionStatusIcon status={includesModifierFilterValue![0]} />
									{procedureStepCompletionStatusData[includesModifierFilterValue![0]].label}
								</>
							)
						default:
							return (
								<>
									<ProcedureStepCompletionStatusIcons statuses={includesModifierFilterValue!} />
									{`${includesModifierFilterValue!.length} statuses`}
								</>
							)
					}
				}

				return (
					<ProcedureStepCompletionStatusMultiSelect
						values={includesModifierFilterValue ?? EMPTY_ARRAY}
						onValuesChange={handleValuesChange}
						size={size}
					>
						<FilterCellButton className="truncate">
							<span className="flex items-center gap-2 truncate">{getLabelUi()}</span>
						</FilterCellButton>
					</ProcedureStepCompletionStatusMultiSelect>
				)
			}
		}
	}

	return (
		<FilterCellRoot>
			<FilterCellBadge>
				<LuIcon icon={modelIconMapping.stage} color={procedureStep.color} fill={procedureStep.color} />
				{procedureStep.name} - Completion
			</FilterCellBadge>

			<FilterModifierDropdown filterDef={filterDef} filter={filter}>
				<FilterCellButton>
					{filterDef.getFilterModifier(filterModifier).modifier.render(filterValue)}
				</FilterCellButton>
			</FilterModifierDropdown>

			{getFilterValueUi()}

			<RemoveFilterButton filter={filter} filterManager={filterManager} />
		</FilterCellRoot>
	)
}
